import type { Page, Pageable } from '../../../core/api/spring-types';
import { normalizePage } from '../../../core/api/spring-types';
import type {
    NotificationReportDto,
    NotificationDto,
    NotificationReadDto,
    InboxNotificationDto
} from '../../../api/generated/types.gen';
import {
    getAllNotifications,
    getNotificationsReport,
    getInboxNotifications,
    getNotificationById,
    getDefaultNotification,
    createNotification,
    updateNotification,
    deleteNotification,
    resendNotification,
    markNotificationAsRead,
    getNotificationReceiversStatus,
} from '../../../api/generated/sdk.gen';
import { handleResponse } from '@/core/api/response-handler';

// Export types for external use
export type { NotificationReportDto, NotificationDto, InboxNotificationDto };

/**
 * Notifications API - Thin wrapper for auto-generated SDK functions
 * Maintains backward compatibility with existing hook signatures
 */
class NotificationsAPI {
    /**
     * Inbox-ul propriu al utilizatorului logat.
     *
     * NU inlocui asta cu getReport({'receiver.id': ...}): /report e inchis la DBEST+ADMIN din
     * 2026-08-28, pentru ca filtrarea lui pe destinatar vine dintr-un parametru al cererii, deci
     * poate fi schimbat de apelant ca sa citeasca inbox-ul altuia. /inbox se scopeaza server-side,
     * pe utilizatorul din token. Confuzia asta a rupt inbox-ul in productie (defectul D3, T7-S12).
     *
     * Ordinea (necitite intai, apoi cele mai noi) e fixata pe server; de aceea nu se trimite `sort`.
     */
    async getInbox(pageable: Pageable): Promise<Page<InboxNotificationDto>> {
        const response = await getInboxNotifications({
            query: { page: pageable.page, size: pageable.size } as any,
        });
        return normalizePage(handleResponse(response));
    }

    // Report endpoint
    async getReport(
        filter: Partial<NotificationReportDto>,
        pageable: Pageable
    ): Promise<Page<NotificationReportDto>> {
        const response = await getNotificationsReport({
            query: { ...filter, ...pageable } as any,
        });
        return normalizePage(handleResponse(response));
    }

    // CRUD operations
    async getAll(): Promise<NotificationDto[]> {
        const response = await getAllNotifications();
        return handleResponse(response);
    }

    async getById(id: string): Promise<NotificationDto> {
        const response = await getNotificationById({
            path: { id },
        });
        return handleResponse(response);
    }

    async create(data: NotificationDto): Promise<NotificationDto> {
        const response = await createNotification({
            body: data,
        });
        return handleResponse(response);
    }

    async update(data: NotificationDto): Promise<NotificationDto> {
        const response = await updateNotification({
            body: data,
        });
        return handleResponse(response);
    }

    async delete(id: string): Promise<void> {
        const response = await deleteNotification({
            path: { id },
        });
        handleResponse(response);
    }

    async resend(id: string): Promise<NotificationDto> {
        const response = await resendNotification({
            path: { id },
        });
        return handleResponse(response);
    }

    async markAsRead(id: string, isRead: boolean = true): Promise<void> {
        const response = await markNotificationAsRead({
            path: { id },
            query: { isRead },
        });
        handleResponse(response);
    }

    // Get notification receivers with delivery status
    async getReceivers(id: string): Promise<NotificationReadDto[]> {
        const response = await getNotificationReceiversStatus({
            path: { id },
        });
        return handleResponse(response);
    }

    // Get default notification template
    async getDefault(): Promise<NotificationDto> {
        const response = await getDefaultNotification();
        return handleResponse(response);
    }

    // Bulk create notifications
    async createBulk(data: NotificationDto): Promise<NotificationDto[]> {
        const response = await createNotification({
            body: data,
        });
        return [handleResponse(response)] as NotificationDto[];
    }
}

export const notificationsApi = new NotificationsAPI();
